import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { entitlementService } from '../services/entitlement';

/**
 * Debug page for testing entitlement configuration
 * Access at /entitlement-debug
 */
export default function EntitlementDebugPage() {
  const { user } = useAuth();
  const [debugInfo, setDebugInfo] = useState<any>(null);
  const [statusInfo, setStatusInfo] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDebugInfo = async () => {
    setLoading(true);
    setError(null);
    try {
      const debug = await entitlementService.getDebugInfo();
      setDebugInfo(debug);
      console.log('Debug Info:', debug);
    } catch (err: any) {
      setError(`Debug endpoint error: ${err.message}`);
      console.error('Debug endpoint error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatusInfo = async () => {
    setLoading(true);
    setError(null);
    try {
      const status = await entitlementService.getStatus();
      setStatusInfo(status);
      console.log('Status Info:', status);
    } catch (err: any) {
      setError(`Status endpoint error: ${err.message}`);
      console.error('Status endpoint error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchBoth = async () => {
    setLoading(true);
    setError(null);
    try {
      const [debug, status] = await Promise.all([
        entitlementService.getDebugInfo(),
        entitlementService.getStatus(),
      ]);
      setDebugInfo(debug);
      setStatusInfo(status);
      console.log('Debug Info:', debug);
      console.log('Status Info:', status);
    } catch (err: any) {
      setError(`Error: ${err.message}`);
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-xl p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Entitlement Debug Console
          </h1>
          <p className="text-gray-600 mb-6">
            Test entitlement endpoints with authenticated requests
          </p>

          {/* User Info */}
          <div className="bg-blue-50 rounded-lg p-4 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Current User</h2>
            <div className="space-y-1 text-sm">
              <div>
                <span className="font-medium">Authenticated:</span>{' '}
                <span className={user ? 'text-green-600' : 'text-red-600'}>
                  {user ? 'Yes' : 'No'}
                </span>
              </div>
              {user && (
                <>
                  <div>
                    <span className="font-medium">Firebase UID:</span>{' '}
                    <code className="bg-gray-100 px-2 py-0.5 rounded">{user.uid}</code>
                  </div>
                  <div>
                    <span className="font-medium">Email:</span> {user.email}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 mb-6">
            <button
              onClick={fetchDebugInfo}
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Fetch Debug Info
            </button>
            <button
              onClick={fetchStatusInfo}
              disabled={loading}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Fetch Status Info
            </button>
            <button
              onClick={fetchBoth}
              disabled={loading}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Fetch Both
            </button>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-gray-600">Loading...</p>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <h3 className="text-red-800 font-semibold mb-1">Error</h3>
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {/* Debug Info */}
          {debugInfo && (
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-3">
                Debug Info
              </h2>
              <div className="bg-gray-50 rounded-lg p-4 overflow-auto">
                <pre className="text-xs text-gray-800 whitespace-pre-wrap">
                  {JSON.stringify(debugInfo, null, 2)}
                </pre>
              </div>

              {/* Key Insights */}
              <div className="mt-4 space-y-2">
                <div className="flex items-center gap-2">
                  <span className="font-medium">Is Test User:</span>
                  <span
                    className={
                      debugInfo.testUserConfig?.currentUserIsTestUser
                        ? 'text-green-600 font-semibold'
                        : 'text-red-600 font-semibold'
                    }
                  >
                    {debugInfo.testUserConfig?.currentUserIsTestUser ? 'YES ✓' : 'NO ✗'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">Enforcement Active:</span>
                  <span
                    className={
                      debugInfo.enforcement?.active
                        ? 'text-green-600 font-semibold'
                        : 'text-red-600 font-semibold'
                    }
                  >
                    {debugInfo.enforcement?.active ? 'YES ✓' : 'NO ✗'}
                  </span>
                </div>
                {debugInfo.enforcement?.reason && (
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">Reason:</span> {debugInfo.enforcement.reason}
                  </div>
                )}
                {/* Total Comparisons Count */}
                {debugInfo.user && (
                  <div className="flex items-center gap-2 pt-2 border-t border-gray-200">
                    <span className="font-medium">Total Comparisons Stored:</span>
                    <span className="text-blue-600 font-semibold">
                      {debugInfo.user.totalComparisonsCount ?? 0}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Status Info */}
          {statusInfo && (
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">
                Status Info
              </h2>
              <div className="bg-gray-50 rounded-lg p-4 overflow-auto">
                <pre className="text-xs text-gray-800 whitespace-pre-wrap">
                  {JSON.stringify(statusInfo, null, 2)}
                </pre>
              </div>

              {/* Key Insights */}
              {statusInfo.plan && (
                <div className="mt-4 space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">Plan:</span>
                    <span className="text-blue-600 font-semibold">
                      {statusInfo.plan.displayName} ({statusInfo.plan.code})
                    </span>
                  </div>
                  {statusInfo.quotas?.comparisons && (
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Daily Comparisons:</span>
                      <span className="text-gray-900">
                        {statusInfo.quotas.comparisons.used} of{' '}
                        {statusInfo.quotas.comparisons.limit} used
                        {' ('}
                        {statusInfo.quotas.comparisons.remaining} remaining)
                      </span>
                    </div>
                  )}
                  {/* Total Comparisons Stored */}
                  {statusInfo.totalComparisonsCount !== undefined && (
                    <div className="flex items-center gap-2 bg-blue-50 rounded p-2 border border-blue-200">
                      <span className="font-medium">Total Comparisons Stored:</span>
                      <span className="text-blue-600 font-bold">
                        {statusInfo.totalComparisonsCount}
                      </span>
                      {statusInfo.quotas?.savedComparisons && (
                        <span className="text-gray-600">
                          {' '}of {statusInfo.quotas.savedComparisons.limit} max
                          ({statusInfo.quotas.savedComparisons.percentage}%)
                        </span>
                      )}
                    </div>
                  )}
                  {/* Saved Comparisons Quota */}
                  {statusInfo.quotas?.savedComparisons && (
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Storage Quota:</span>
                      <span className="text-gray-900">
                        {statusInfo.quotas.savedComparisons.count} of{' '}
                        {statusInfo.quotas.savedComparisons.limit} used
                        {' ('}
                        {statusInfo.quotas.savedComparisons.remaining} remaining)
                      </span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <span className="font-medium">Paywall Enabled:</span>
                    <span
                      className={
                        statusInfo.paywallEnabled
                          ? 'text-green-600 font-semibold'
                          : 'text-gray-600'
                      }
                    >
                      {statusInfo.paywallEnabled ? 'YES' : 'NO'}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
